import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useRateCard } from '@/hooks/useRateCard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronRight, ChevronDown, Receipt, History, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { calculateMargin } from '@/types/rateCard.types';
import type { RoleRate, AssignedPerson, RateChangeHistoryEntry } from '@/types/rateCard.types';
import { toast } from 'sonner';
import { useBudgetPermissions } from '@/hooks/use-budget-permissions';
import { fetchProjectById } from '@/lib/data-service';
import {
  fetchRateChangeBudgetImpact,
  applyRateChangeBudgetCascade,
  type EffectiveRoleRate,
  type RateChangeBudgetImpact,
  type RateChangeCascadeDecision,
} from '@/services/rateChangeCascade.service';
import { RateChangeCascadeDialog } from './RateChangeCascadeDialog';
import type { ProjectType } from '@/types';

interface RateCardPanelProps {
  projectId: string;
  scrollToRoleId?: string;
  onChangesChange?: (hasChanges: boolean) => void;
  onSaveRef?: (ref: { save: () => void; cancel: () => void } | null) => void;
}

export const RateCardPanel = ({ projectId, scrollToRoleId, onChangesChange, onSaveRef }: RateCardPanelProps) => {
  const { summary, roleRates, rateChangeHistory, loading, historyLoading, updateRoleRate, updatePersonRate, loadRateChangeHistory, refresh } = useRateCard(projectId);
  const { settings: budgetSettings, isCostCentreMode } = useBudgetPermissions();
  
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(scrollToRoleId || null);
  const [editedRoleRates, setEditedRoleRates] = useState<Record<string, Partial<RoleRate>>>({});
  const [editedPersonRates, setEditedPersonRates] = useState<Record<string, Partial<AssignedPerson>>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notes, setNotes] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [projectType, setProjectType] = useState<ProjectType | undefined>(undefined);
  const [cascadeImpact, setCascadeImpact] = useState<RateChangeBudgetImpact | null>(null);
  const [cascadeNotes, setCascadeNotes] = useState('');
  const [isCascading, setIsCascading] = useState(false);

  const saveCallbackRef = useRef<() => void>(() => {});
  const cancelCallbackRef = useRef<() => void>(() => {});

  // Scroll to role if specified
  useEffect(() => {
    if (scrollToRoleId) {
      setExpandedRoleId(scrollToRoleId);
      setTimeout(() => {
        const element = document.getElementById(`role-${scrollToRoleId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  }, [scrollToRoleId]);

  useEffect(() => {
    onChangesChange?.(hasChanges);
  }, [hasChanges, onChangesChange]);

  // Project type decides whether the Sales Budget cascade is offered.
  useEffect(() => {
    let cancelled = false;
    fetchProjectById(projectId)
      .then(project => {
        if (cancelled) return;
        setProjectType((project?.type ?? project?.projectType) as ProjectType | undefined);
      })
      .catch(() => setProjectType(undefined));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (onSaveRef) {
      onSaveRef({
        save: () => saveCallbackRef.current(),
        cancel: () => cancelCallbackRef.current(),
      });
    }
    return () => onSaveRef?.(null);
  }, [onSaveRef]);

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading rates...</div>;
  }

  const toggleExpand = (roleId: string) => {
    setExpandedRoleId(expandedRoleId === roleId ? null : roleId);
  };

  const getBadgeVariant = (source: string) => {
    switch (source) {
      case 'Project Override':
        return 'default';
      case 'Person Override':
        return 'outline';
      case 'Client Agreed':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const handleRoleRateChange = (roleId: string, field: string, value: string) => {
    setEditedRoleRates(prev => {
      const current = prev[roleId] || {};
      const role = roleRates.find(r => r.roleId === roleId);
      if (!role) return prev;

      let updated: any;
      
      // Handle date field differently
      if (field === 'effectiveFrom') {
        updated = { ...current, [field]: value };
      } else {
        // Handle numeric fields
        const numValue = parseFloat(value) || 0;
        updated = { ...current, [field]: numValue };
        
        // Recalculate margin if cost or bill rate changed
        if (field === 'hourlyCostRate' || field === 'hourlyBillRate') {
          const costRate = field === 'hourlyCostRate' ? numValue : (updated.hourlyCostRate ?? role.hourlyCostRate);
          const billRate = field === 'hourlyBillRate' ? numValue : (updated.hourlyBillRate ?? role.hourlyBillRate);
          updated.margin = calculateMargin(billRate, costRate);
        }
        
        if (field === 'dailyCostRate' || field === 'dailyBillRate') {
          const costRate = field === 'dailyCostRate' ? numValue : (updated.dailyCostRate ?? role.dailyCostRate);
          const billRate = field === 'dailyBillRate' ? numValue : (updated.dailyBillRate ?? role.dailyBillRate);
          updated.margin = calculateMargin(billRate, costRate);
        }
      }

      return { ...prev, [roleId]: updated };
    });
    
    setHasChanges(true);
  };

  const handlePersonRateChange = (roleId: string, personId: string, field: string, value: string) => {
    const key = `${roleId}-${personId}`;
    
    setEditedPersonRates(prev => {
      const current = prev[key] || {};
      const role = roleRates.find(r => r.roleId === roleId);
      const person = role?.assignedPeople.find(p => p.personId === personId);
      if (!person) return prev;

      let updated: any;
      
      // Handle date field differently
      if (field === 'effectiveFrom') {
        updated = { ...current, [field]: value };
      } else {
        // Handle numeric fields
        const numValue = parseFloat(value) || 0;
        updated = { ...current, [field]: numValue };
        
        // Recalculate margin if cost or bill rate changed
        if (field === 'hourlyCostRate' || field === 'hourlyBillRate') {
          const costRate = field === 'hourlyCostRate' ? numValue : (updated.hourlyCostRate ?? person.hourlyCostRate);
          const billRate = field === 'hourlyBillRate' ? numValue : (updated.hourlyBillRate ?? person.hourlyBillRate);
          updated.margin = calculateMargin(billRate, costRate);
        }
      }

      return { ...prev, [key]: updated };
    });
    
    setHasChanges(true);
  };

  const validateRates = (): boolean => {
    const errors: Record<string, string> = {};

    // Validate role rates
    Object.entries(editedRoleRates).forEach(([roleId, rates]) => {
      if (rates.hourlyCostRate !== undefined && rates.hourlyCostRate <= 0) {
        errors[`role-${roleId}`] = 'Cost rate must be greater than 0';
      }
      if (rates.hourlyBillRate !== undefined && rates.hourlyBillRate < 0) {
        errors[`role-${roleId}`] = 'Bill rate cannot be negative';
      }
    });

    // Validate person rates
    Object.entries(editedPersonRates).forEach(([key, rates]) => {
      if (rates.hourlyCostRate !== undefined && rates.hourlyCostRate <= 0) {
        errors[`person-${key}`] = 'Cost rate must be greater than 0';
      }
      if (rates.hourlyBillRate !== undefined && rates.hourlyBillRate < 0) {
        errors[`person-${key}`] = 'Bill rate cannot be negative';
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveAll = () => {
    if (!validateRates()) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    setShowNotesDialog(true);
  };

  /** Rate Card rates as they will be once the pending edits are saved. */
  const buildEffectiveRoleRates = (): EffectiveRoleRate[] =>
    roleRates.map(role => {
      const edited = editedRoleRates[role.roleId] ?? {};
      return {
        roleId: parseInt(role.roleId.replace('role-', ''), 10),
        roleName: role.roleName,
        hourlyCostRate: edited.hourlyCostRate ?? role.hourlyCostRate,
        hourlyBillRate: edited.hourlyBillRate ?? role.hourlyBillRate,
      };
    });

  const handleConfirmSave = async () => {
    let changeCount = 0;
    const effectiveRoleRates = buildEffectiveRoleRates();

    // Save role rate changes
    for (const [roleId, rates] of Object.entries(editedRoleRates)) {
      const role = roleRates.find(r => r.roleId === roleId);
      if (!role) continue;

      const saved = await updateRoleRate({
        roleId,
        hourlyCostRate: rates.hourlyCostRate ?? role.hourlyCostRate,
        hourlyBillRate: rates.hourlyBillRate ?? role.hourlyBillRate,
        dailyCostRate: rates.dailyCostRate ?? role.dailyCostRate,
        dailyBillRate: rates.dailyBillRate ?? role.dailyBillRate,
        effectiveFrom: rates.effectiveFrom ?? role.effectiveFrom,
        notes,
      }, { reload: false });
      if (saved) changeCount++;
    }

    // Save person rate changes
    for (const [key, rates] of Object.entries(editedPersonRates)) {
      const lastDash = key.lastIndexOf('-');
      const roleId = key.substring(0, lastDash);
      const personId = key.substring(lastDash + 1);
      const role = roleRates.find(r => r.roleId === roleId);
      const person = role?.assignedPeople.find(p => p.personId === personId);
      if (!person) continue;

      const saved = await updatePersonRate({
        roleId,
        personId,
        personName: person.personName,
        hourlyCostRate: rates.hourlyCostRate ?? person.hourlyCostRate,
        hourlyBillRate: rates.hourlyBillRate ?? person.hourlyBillRate,
        dailyCostRate: rates.dailyCostRate ?? person.dailyCostRate,
        dailyBillRate: rates.dailyBillRate ?? person.dailyBillRate,
        effectiveFrom: rates.effectiveFrom ?? person.effectiveFrom,
        notes,
      }, { reload: false });
      if (saved) changeCount++;
    }

    // Reset state
    setEditedRoleRates({});
    setEditedPersonRates({});
    setHasChanges(false);
    setShowNotesDialog(false);
    setCascadeNotes(notes);
    setNotes('');
    setValidationErrors({});

    toast.success(`Successfully saved ${changeCount} rate change${changeCount !== 1 ? 's' : ''}`);

    await refresh();
    window.dispatchEvent(new Event('rateCardUpdated'));

    // Offer to update the Cost / Sales Labour budgets for the new rates, the
    // same way a Resource Budget revision does.
    try {
      const impact = await fetchRateChangeBudgetImpact(projectId, effectiveRoleRates);
      if (impact?.hasChanges) {
        setCascadeImpact(impact);
      }
    } catch (error) {
      console.error('[RateCardPanel] Failed to calculate budget impact of rate change:', error);
    }
  };

  const handleCascadeConfirm = async (decision: RateChangeCascadeDecision) => {
    if (!cascadeImpact) return;

    if (!decision.updateCostLabour && !decision.updateSalesLabour) {
      setCascadeImpact(null);
      return;
    }

    setIsCascading(true);
    try {
      await applyRateChangeBudgetCascade(projectId, cascadeImpact, decision, cascadeNotes);
      window.dispatchEvent(new Event('budgetDataUpdated'));
      toast.success('Labour budgets updated for the new rates');
      setCascadeImpact(null);
    } catch (error) {
      console.error('[RateCardPanel] Failed to cascade rate change into budgets:', error);
      toast.error('Failed to update the Labour budgets for the new rates');
    } finally {
      setIsCascading(false);
    }
  };

  const handleCancel = () => {
    setEditedRoleRates({});
    setEditedPersonRates({});
    setHasChanges(false);
    setValidationErrors({});
  };

  saveCallbackRef.current = handleSaveAll;
  cancelCallbackRef.current = handleCancel;

  const getDisplayValue= (roleId: string, field: keyof RoleRate): number => {
    const edited = editedRoleRates[roleId];
    if (edited && edited[field] !== undefined) {
      return edited[field] as number;
    }
    const role = roleRates.find(r => r.roleId === roleId);
    return role ? (role[field] as number) : 0;
  };

  const getPersonDisplayValue = (roleId: string, personId: string, field: keyof AssignedPerson): number => {
    const key = `${roleId}-${personId}`;
    const edited = editedPersonRates[key];
    if (edited && edited[field] !== undefined) {
      return edited[field] as number;
    }
    const role = roleRates.find(r => r.roleId === roleId);
    const person = role?.assignedPeople.find(p => p.personId === personId);
    return person ? (person[field] as number) : 0;
  };

  return (
    <div className="px-6 py-4">
      <Tabs defaultValue="rates">
        <TabsList className="mb-4">
          <TabsTrigger value="rates">
            <Receipt className="mr-2 h-4 w-4" />
            Rates
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="mr-2 h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="rates" className="space-y-4 mt-0">
          <div className="flex items-center justify-between text-sm text-muted-foreground border-b pb-2">
            <span>{summary.roleCount} roles configured</span>
            <div className="flex gap-4">
              <span>Avg Bill: {formatCurrency(summary.averageHourlyBillRate)}/hr</span>
              <span>Avg Cost: {formatCurrency(summary.averageHourlyCostRate)}/hr</span>
            </div>
          </div>

          <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[50px]"></TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Hierarchy</TableHead>
                    <TableHead>Cost Hr</TableHead>
                    <TableHead>Bill Hr</TableHead>
                    <TableHead>Cost Day</TableHead>
                    <TableHead>Bill Day</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roleRates.map((role) => (
                    <React.Fragment key={role.roleId}>
                      {/* Role Row */}
                      <TableRow id={`role-${role.roleId}`} className="hover:bg-muted/50 h-10">
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleExpand(role.roleId)}
                          >
                            {expandedRoleId === role.roleId ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium">{role.roleName}</TableCell>
                        <TableCell>
                          {role.source !== 'Org Default' && (
                            <Badge variant={getBadgeVariant(role.source)}>
                              {role.source}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="py-1">
                          <Input
                            type="number"
                            value={getDisplayValue(role.roleId, 'hourlyCostRate')}
                            onChange={(e) => handleRoleRateChange(role.roleId, 'hourlyCostRate', e.target.value)}
                            className="w-24 h-8 px-2 py-1"
                          />
                        </TableCell>
                        <TableCell className="py-1">
                          <Input
                            type="number"
                            value={getDisplayValue(role.roleId, 'hourlyBillRate')}
                            onChange={(e) => handleRoleRateChange(role.roleId, 'hourlyBillRate', e.target.value)}
                            className="w-24 h-8 px-2 py-1"
                          />
                        </TableCell>
                        <TableCell className="py-1">
                          <Input
                            type="number"
                            value={getDisplayValue(role.roleId, 'dailyCostRate')}
                            onChange={(e) => handleRoleRateChange(role.roleId, 'dailyCostRate', e.target.value)}
                            className="w-24 h-8 px-2 py-1"
                          />
                        </TableCell>
                        <TableCell className="py-1">
                          <Input
                            type="number"
                            value={getDisplayValue(role.roleId, 'dailyBillRate')}
                            onChange={(e) => handleRoleRateChange(role.roleId, 'dailyBillRate', e.target.value)}
                            className="w-24 h-8 px-2 py-1"
                          />
                        </TableCell>
                        <TableCell className="py-1">
                          <Input
                            type="date"
                            value={
                              editedRoleRates[role.roleId]?.effectiveFrom 
                                ? editedRoleRates[role.roleId].effectiveFrom 
                                : role.effectiveFrom.split('T')[0]
                            }
                            onChange={(e) => handleRoleRateChange(role.roleId, 'effectiveFrom', e.target.value)}
                            className="w-36 h-8 px-2 py-1"
                          />
                        </TableCell>
                      </TableRow>

                      {/* Validation Error Row for Role */}
                      {validationErrors[`role-${role.roleId}`] && (
                        <TableRow className="bg-error-50">
                          <TableCell></TableCell>
                          <TableCell colSpan={7}>
                            <div className="flex items-center gap-2 text-error-600 text-sm">
                              <AlertCircle className="h-4 w-4" />
                              {validationErrors[`role-${role.roleId}`]}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}

                      {/* Expanded Assigned People Rows */}
                      {expandedRoleId === role.roleId && role.assignedPeople.map((person) => (
                        <React.Fragment key={person.personId}>
                          <TableRow className="bg-muted/30 h-10">
                            <TableCell></TableCell>
                            <TableCell className="pl-8 text-sm text-muted-foreground">
                              {person.personName}
                            </TableCell>
                            <TableCell>
                              {person.source !== 'Org Default' && (
                                <Badge variant={getBadgeVariant(person.source)} className="text-xs">
                                  {person.source}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="py-1">
                              <Input
                                type="number"
                                value={getPersonDisplayValue(role.roleId, person.personId, 'hourlyCostRate')}
                                onChange={(e) => handlePersonRateChange(role.roleId, person.personId, 'hourlyCostRate', e.target.value)}
                                className="w-24 h-8 px-2 py-1 text-sm"
                              />
                            </TableCell>
                            <TableCell className="py-1">
                              <Input
                                type="number"
                                value={getPersonDisplayValue(role.roleId, person.personId, 'hourlyBillRate')}
                                onChange={(e) => handlePersonRateChange(role.roleId, person.personId, 'hourlyBillRate', e.target.value)}
                                className="w-24 h-8 px-2 py-1 text-sm"
                              />
                            </TableCell>
                            <TableCell className="py-1">
                              <Input
                                type="number"
                                value={getPersonDisplayValue(role.roleId, person.personId, 'dailyCostRate')}
                                onChange={(e) => handlePersonRateChange(role.roleId, person.personId, 'dailyCostRate', e.target.value)}
                                className="w-24 h-8 px-2 py-1 text-sm"
                              />
                            </TableCell>
                            <TableCell className="py-1">
                              <Input
                                type="number"
                                value={getPersonDisplayValue(role.roleId, person.personId, 'dailyBillRate')}
                                onChange={(e) => handlePersonRateChange(role.roleId, person.personId, 'dailyBillRate', e.target.value)}
                                className="w-24 h-8 px-2 py-1 text-sm"
                              />
                            </TableCell>
                            <TableCell className="py-1">
                              <Input
                                type="date"
                                value={
                                  editedPersonRates[`${role.roleId}-${person.personId}`]?.effectiveFrom
                                    ? editedPersonRates[`${role.roleId}-${person.personId}`].effectiveFrom
                                    : person.effectiveFrom.split('T')[0]
                                }
                                onChange={(e) => handlePersonRateChange(role.roleId, person.personId, 'effectiveFrom', e.target.value)}
                                className="w-36 h-8 px-2 py-1 text-sm"
                              />
                            </TableCell>
                          </TableRow>

                          {/* Validation Error Row for Person */}
                          {validationErrors[`person-${role.roleId}-${person.personId}`] && (
                            <TableRow className="bg-error-50">
                              <TableCell></TableCell>
                              <TableCell colSpan={7}>
                                <div className="flex items-center gap-2 text-error-600 text-sm">
                                  <AlertCircle className="h-4 w-4" />
                                  {validationErrors[`person-${role.roleId}-${person.personId}`]}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </React.Fragment>
                  ))}
                </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="history" className="mt-0">
          <RateChangeHistoryTab
            history={rateChangeHistory}
            loading={historyLoading}
            onLoad={loadRateChangeHistory}
          />
        </TabsContent>
      </Tabs>

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Notes (Optional)</DialogTitle>
            <DialogDescription>
              Add a note to explain why these rates are changing. This will be saved in the rate history.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            placeholder="e.g., Annual rate increase, Client negotiation, Market adjustment..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotesDialog(false)}>
              Skip
            </Button>
            <Button onClick={handleConfirmSave}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cost / Sales Labour cascade prompt */}
      {cascadeImpact && (
        <RateChangeCascadeDialog
          open
          impact={cascadeImpact}
          cascadeSettings={budgetSettings.revisionSettings}
          isSaving={isCascading}
          projectType={projectType}
          isCostCentreMode={isCostCentreMode}
          onConfirm={handleCascadeConfirm}
          onCancel={() => setCascadeImpact(null)}
        />
      )}
    </div>
  );
};

interface RateChangeHistoryTabProps {
  history: RateChangeHistoryEntry[];
  loading: boolean;
  onLoad: () => void;
}

const RateChangeHistoryTab = ({ history, loading, onLoad }: RateChangeHistoryTabProps) => {
  const hasLoaded = React.useRef(false);

  React.useEffect(() => {
    if (!hasLoaded.current) {
      hasLoaded.current = true;
      onLoad();
    }
  }, [onLoad]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading history...
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No rate changes yet
      </div>
    );
  }

  const formatRate = (value: number | null): string => {
    if (value === null || value === undefined) return '-';
    return formatCurrency(value);
  };

  const formatDate = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-muted-foreground border-b pb-2">
        {history.length} change{history.length !== 1 ? 's' : ''} recorded
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Role / Person</TableHead>
            <TableHead>Cost Hr</TableHead>
            <TableHead>Bill Hr</TableHead>
            <TableHead>Cost Day</TableHead>
            <TableHead>Bill Day</TableHead>
            <TableHead>Changed By</TableHead>
            <TableHead>Comment</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="whitespace-nowrap text-sm">
                <div>{formatDate(entry.changedAt)}</div>
                <div className="text-xs text-muted-foreground">{formatTime(entry.changedAt)}</div>
              </TableCell>
              <TableCell>
                <Badge variant={entry.changeType === 'Person' ? 'outline' : 'default'}>
                  {entry.changeType}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="font-medium text-sm">{entry.roleName}</div>
                {entry.employeeName && (
                  <div className="text-xs text-muted-foreground">{entry.employeeName}</div>
                )}
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">{formatRate(entry.costRateBefore)}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span>{formatRate(entry.costRateAfter)}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">{formatRate(entry.billRateBefore)}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span>{formatRate(entry.billRateAfter)}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">{formatRate(entry.dailyCostRateBefore)}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span>{formatRate(entry.dailyCostRateAfter)}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div className="flex items-center gap-1">
                  <span className="text-muted-foreground">{formatRate(entry.dailyBillRateBefore)}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span>{formatRate(entry.dailyBillRateAfter)}</span>
                </div>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {entry.changedBy ?? '-'}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {entry.comment ?? '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
